/**
 * Payment data-access layer for CNP-133. ALL functions are stubs returning typed fixtures.
 *
 * The real flow is two-step (verify→create), confirmed against legacy mdvipconnect and the
 * backend schema: raw PAN/CVV/account go ONLY to the verify step; create receives a gateway
 * token + masked data. `verifyCreditCardInformation` is already live in the backend (CNP-184/185),
 * but create/verifyACH/billing-profile are pending — so everything is stubbed here for now.
 *
 * Phase 9 swap: replace each function body with `gqlRequest(...Document)`. The shapes below
 * mirror the real contracts so the swap is mechanical.
 */
import type { BankAccountType, BillingAddressValues } from '@/lib/payments/paymentAccount.types';

/** Gateway verify result — matches the live `verifyCreditCardInformation` success type. */
export interface VerifyResult {
  gatewayTokenId: string;
  authTransactionId: string;
  /** PaymentProfilePaymentType: ACH | AMEX | DISC | MC | VISA (gateway-authoritative). */
  paymentType: string;
}

export interface VerifyCardRequest {
  cardNumber: string;
  cvv: string;
  expirationMonth: number;
  expirationYear: number;
}

export interface VerifyAchRequest {
  accountNumber: string;
  routingNumber: string;
}

export interface CreateCardProfileRequest {
  creditCardInfo: {
    last4: string;
    nameOnCard: string;
    hsa: boolean;
    expirationMonth: number;
    expirationYear: number;
    paymentType: string;
  };
  gatewayTokenId: string;
  authTransactionId: string;
  isDefault: boolean;
  autoPay: boolean;
  billing: BillingAddressValues;
}

export interface CreateBankProfileRequest {
  achInfo: {
    last4: string;
    bankName: string;
    accountName: string;
    routingNumber: string;
    accountType: BankAccountType;
    hsa: boolean;
  };
  gatewayTokenId: string;
  authTransactionId: string;
  isDefault: boolean;
  autoPay: boolean;
  billing: BillingAddressValues;
}

export interface CreateProfileResult {
  id: string;
}

export interface MemberBillingProfile {
  savedAddress: BillingAddressValues | null;
  autoPayEnrolled: boolean;
  /** Annual fee (legacy `Current_Agreement_MSRP__c`) — review-screen source TBD with BE. */
  autoPayAmount: number | null;
  /** Payment frequency (legacy `Current_Agreement_Payment_Schedule__c`). */
  autoPayPlan: string | null;
}

// --- Stubs. Simulated latency so loading states are exercisable in dev. ---
const STUB_LATENCY_MS = 400;
const delay = () => new Promise((resolve) => setTimeout(resolve, STUB_LATENCY_MS));

// TODO(CNP-184/185): swap to gqlRequest(VerifyCreditCardInformationDocument, { input }).
export async function requestVerifyCard(_input: VerifyCardRequest): Promise<VerifyResult> {
  await delay();
  return { gatewayTokenId: 'stub-card-token', authTransactionId: 'stub-card-auth', paymentType: 'VISA' };
}

// TODO(BE): swap to gqlRequest(VerifyACHInformationDocument, { input }).
export async function requestVerifyAch(_input: VerifyAchRequest): Promise<VerifyResult> {
  await delay();
  return { gatewayTokenId: 'stub-ach-token', authTransactionId: 'stub-ach-auth', paymentType: 'ACH' };
}

// TODO(CNP-129): swap to gqlRequest(CreateNewPaymentProfileDocument, { input }).
export async function requestCreateCardProfile(_input: CreateCardProfileRequest): Promise<CreateProfileResult> {
  await delay();
  return { id: 'stub-card-profile-id' };
}

// TODO(CNP-130): swap to gqlRequest(CreateNewPaymentProfileDocument, { input }).
export async function requestCreateBankProfile(_input: CreateBankProfileRequest): Promise<CreateProfileResult> {
  await delay();
  return { id: 'stub-bank-profile-id' };
}

// TODO(BE): swap to gqlRequest(MemberBillingProfileDocument) — fields off the member object.
export async function requestMemberBillingProfile(): Promise<MemberBillingProfile> {
  await delay();
  return {
    savedAddress: {
      name: 'Jane Member',
      line1: '123 Ocean Dr',
      line2: '',
      city: 'Boca Raton',
      state: 'FL',
      zip: '33458',
    },
    autoPayEnrolled: false,
    autoPayAmount: 2600,
    autoPayPlan: 'Quarterly',
  };
}
